// Brokers.tsx
import React, { useState, useEffect, useMemo, useRef, memo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from "@mui/material";
import { KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";
import MDButton from "../../components/MDButton";
import { states, validateZipOrPostalCode } from "../../utils/States";
import { SelectChangeEvent } from "@mui/material";
import ExcelExportUtil from "../../utils/DataExportUtils";
import { get, post } from "requests";
import withDommer from "../withDommer";
import BrokersHeader from "./brokersFilterComponent";
import { formatServerDate, formatToMMDDYY } from "utils/CalanderUtils";
import FullPageLoader from "components/Loader/FullPage";
import CollapsibleBrokersWrapper from "./collapsibleBrokerWrapper";
import moment from "moment";

interface DateFilters {
  fromDate: Date | null;
  toDate: Date | null;
  byPeriod: string | null;
}

interface ValidationRule {
  required: boolean;
  message: string;
  pattern?: RegExp;
  validate?: (value: string) => boolean;
}

interface ValidationRules {
  [key: string]: ValidationRule;
}

interface ValidationErrors {
  name?: string;
  email?: string;
  phoneNumber?: string;
  contactPerson?: string;
  address?: string;
  state?: string;
  city?: string;
  zip?: string;
  abaNumber?: string;
  accountNumber?: string;
  bankName?: string;
}

type SummaryItem = { title: string; value: string; color?: string };

const MIN_CARD_PX = 140;
const GAP_PX = 8;

const FilterCheckbox: React.FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = memo(({ label, checked, onChange }) => (
  <FormControlLabel
    control={<Checkbox checked={checked} onChange={(_, c) => onChange(c)} />}
    label={
      <Typography fontSize="0.875rem" variant="h6">
        {label}
      </Typography>
    }
    sx={{ whiteSpace: "nowrap", marginRight: 0, display: "flex", alignItems: "center" }}
  />
));

const SummaryTile: React.FC<SummaryItem & { width: number }> = ({ title, value, color, width }) => (
  <Box
    sx={{
      flex: `0 0 ${width}px`,
      width: `${width}px`,
      minWidth: MIN_CARD_PX,
      height: 100,
      px: 1,
      py: 1,
      borderRadius: 1.25,
      border: "1px solid #CBD5E1",
      backgroundColor: `${color}08`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.5,
      boxSizing: "border-box",
      "&:hover": {
        transform: "translateY(-2px)",
        backgroundColor: `${color}12`,
      }
    }}
  >
    <Typography
      sx={{
        color: color || "#3D4B62",
        fontWeight: 700,
        fontSize: "28px",
        lineHeight: 1,
        textAlign: "center",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "100%",
      }}
      title={value}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        color: color || "#64748B",
        fontWeight: 400,
        fontSize: "clamp(0.72rem, 0.9vw, 0.82rem)",
        lineHeight: 1.1,
        textAlign: "center",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        maxWidth: "100%",
      }}
      title={title}
    >
      {title}
    </Typography>
  </Box>
);


export const SummaryRow: React.FC<{ items: SummaryItem[] }> = ({ items }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number>(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr?.width != null) setWidth(Math.max(0, Math.floor(cr.width)));
    });
    ro.observe(el);
    setWidth(Math.max(0, Math.floor(el.getBoundingClientRect().width)));
    return () => ro.disconnect();
  }, []);

  const layout = useMemo(() => {
    const n = Math.max(1, items.length);
    const container = Math.max(0, width);
    const colsByMin = Math.max(1, Math.floor((container + GAP_PX) / (MIN_CARD_PX + GAP_PX)));
    const cols = Math.min(n, colsByMin);
    const gaps = (cols - 1) * GAP_PX;
    const cardWidth = Math.max(MIN_CARD_PX, Math.floor((container - gaps) / cols));
    const wrap: "wrap" | "nowrap" = cols < n ? "wrap" : "nowrap";
    return { wrap, cardWidth };
  }, [items.length, width]);

  return (
    <Box
      ref={ref}
      sx={{
        width: "100%",
        display: "flex",
        flexWrap: layout.wrap,
        gap: `${GAP_PX}px`,
        alignItems: "stretch",
        justifyContent: "flex-start",
      }}
    >
      {items.map((it, i) => (
        <SummaryTile key={i} {...it} width={layout.cardWidth} />
      ))}
    </Box>
  );
};

const Brokers: React.FC = () => {
  const [selectedBroker, setSelectedBroker] = useState<any>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [displayLedgerReportAlert, setDisplayLedgerReportAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [brokers, setBrokers] = useState<any[]>([]);
  const [selectedBrokerIds, setSelectedBrokerIds] = useState<number[]>([]);
  const [selectedBrokerNames, setSelectedBrokerNames] = useState<string[]>([]);
  const [dropdownOpen, setdropdownOpen] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedBrokerRow, setSelectedBrokerRow] = useState<any>(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [filters, setFilters] = useState({
    fromDate: null as any,
    toDate: null as any,
    byPeriod: null as any,
    broker: null as any,
    searchTerm: "",
    salesRep: null as any,
  });

  const [filterOptions] = useState<any>([
    { label: "ISO/Broker Shops", value: "shop" },
    { label: "Sales Representatives", value: "rep" },
  ]);

  const [shopRepFilters, setShopRepFilters] = useState<any>({ shop: true, rep: true });

  const [summaryBoxes, setSummaryBoxes] = useState([
    { title: "Commissions Paid", value: "$0", color: "#22C55E" },
    { title: "Commissions Owed", value: "$0", color: "#6B7280" },
    { title:  "Total Open Funded Deals", value: "0", color: "#6B7280" },
    { title: "Total Paid Off Deals", value: "0", color: "#6B7280" },
    { title: "Total Defaulted Deals", value: "0", color: "#6B7280" },
    { title:  "Total Deals Funded", value: "0", color: "#6B7280" },
    { title: "Pending Applications", value: "0", color: "#6B7280" },
    { title: "Declined Applications", value: "-", color: "#6B7280" },
    { title: "Pending Response", value: "-", color: "#6B7280" },
  ]);

  const currency = (n: number) => {
    if (n == null || isNaN(n as any)) return "$0.00";
    const sign = n < 0 ? "-" : "";
    const v = Math.abs(n);
    if (v >= 1_000_000_000) return `${sign}$${(v / 1_000_000_000).toFixed(2)} B`;
    if (v >= 1_000_000) return `${sign}$${(v / 1_000_000).toFixed(2)} M`;
    if (v >= 1_000) return `${sign}$${(v / 1_000).toFixed(2)} K`;
    return `${sign}$${v.toFixed(2)}`;
  };

  const startOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const endOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  };
  function getPeriodRange(byPeriod: string | null) {
    if (!byPeriod || byPeriod === "All Time")
      return { from: null as Date | null, to: null as Date | null };
    const now = new Date();
    const to = endOfDay(now);
    switch (byPeriod) {
      case "Today":
        return { from: startOfDay(now), to };
      case "This Week": {
        const d = new Date(now);
        const day = d.getDay();
        const diffToMon = (day + 6) % 7;
        d.setDate(d.getDate() - diffToMon);
        return { from: startOfDay(d), to };
      }
      case "This Month": {
        const d = new Date(now.getFullYear(), now.getMonth(), 1);
        return { from: startOfDay(d), to };
      }
      case "This Quarter": {
        const q = Math.floor(now.getMonth() / 3);
        const d = new Date(now.getFullYear(), q * 3, 1);
        return { from: startOfDay(d), to };
      }
      case "This Year": {
        const d = new Date(now.getFullYear(), 0, 1);
        return { from: startOfDay(d), to };
      }
      default:
        return { from: null, to: null };
    }
  }
  const includes = (hay: any, needle: string) =>
    String(hay ?? "")
      .toLowerCase()
      .includes(needle.toLowerCase());


  const updateSummaryBoxes = (allRows: any[]) => {
    const shop = !!shopRepFilters.shop;
    const rep = !!shopRepFilters.rep;
    const salesRepApplied = !!(filters.salesRep && String(filters.salesRep).trim().length);
    const searchApplied = !!(filters.searchTerm && String(filters.searchTerm).trim().length);
  
    const brokersOnly = allRows.filter((r) => !!r.isBroker);
    const repsOnly = allRows.filter((r) => !r.isBroker);
  
    const tB = computeTotals(brokersOnly);
    const tR = computeTotals(repsOnly);
    const tC = computeTotals(allRows);
  
    const visibleBrokerIds = new Set(
      brokersOnly.map((b) => String(b.partyId ?? b.id ?? b.brokerId ?? ""))
    );
    const repsWithVisibleBroker = repsOnly.filter((r) =>
      visibleBrokerIds.has(String(r.brokerId ?? ""))
    );
    const tMixNoOrphans = computeTotals([...brokersOnly, ...repsWithVisibleBroker]);
  
    const makeTilesFrom = (t: ReturnType<typeof computeTotals>) => [
      { title: "Commissions Paid", value: currency(t.commissionsPaid), color: "#22C55E" },
      { title: "Commissions Owed", value: currency(t.commissionsOwed), color: "#6B7280" },
      { title: "Total Open Funded Deals", value: String(t.onTime), color: "#6B7280" },
      { title: "Total Paid Off Deals", value: String(t.paidOff), color: "#6B7280" },
      { title: "Total Defaulted Deals", value: String(t.defaults), color: t.defaults > 0 ? "#EF4444" : "#6B7280" },
      { title: "Total Deals Funded", value: String(t.funded), color: "#6B7280" },
      { title: "Pending Applications", value: String(t.pendingApplications), color: "#6B7280" },
      { title: "Declined Applications", value: String(t.declined), color: "#6B7280" },
      { title: "Pending Response", value: String(t.pendingResponse), color: "#6B7280" },
    ];
  
    if (salesRepApplied) {
      setSummaryBoxes(makeTilesFrom(tR));
      return;
    }
  
    if (searchApplied) {
      if (shop && rep) {  
        if (brokersOnly.length && !repsOnly.length) {
          setSummaryBoxes(makeTilesFrom(tB));
          return;
        }
        if (!brokersOnly.length && repsOnly.length) {
          setSummaryBoxes(makeTilesFrom(tR));
          return;
        }
        setSummaryBoxes(makeTilesFrom(tMixNoOrphans));
        return;
      }
      if (shop && !rep) {
        setSummaryBoxes(makeTilesFrom(tB));
        return;
      }
      if (!shop && rep) {
        setSummaryBoxes(makeTilesFrom(tR));
        return;
      }
      setSummaryBoxes(makeTilesFrom(tC));
      return;
    }
  
    if (shop && !rep) {
      setSummaryBoxes(makeTilesFrom(tB));
      return;
    }
    if (!shop && rep) {
      setSummaryBoxes(makeTilesFrom(tR));
      return;
    }
    if (shop && rep) {
      setSummaryBoxes(makeTilesFrom(tB));
      return;
    }
    setSummaryBoxes(makeTilesFrom(tC));
  };
  

  const shownRows = useMemo(() => {
    const shop = !!shopRepFilters.shop;
    const rep = !!shopRepFilters.rep;
    if (!shop && !rep) return [];

    let base = brokers;

    if (selectedBrokerIds.length > 0) {
      const idSet = new Set(selectedBrokerIds);
      base = base.filter((r) =>
        r.isBroker ? idSet.has(r.partyId ?? r.id) : idSet.has(r.brokerId)
      );
    }

    if (filters.salesRep && String(filters.salesRep).trim().length) {
      base = base.filter((r) => !r.isBroker && includes(r.salesRep, String(filters.salesRep)));
    } else {
      base = base.filter((r) => (shop && rep ? true : shop ? !!r.isBroker : !r.isBroker));
    }


    if (filters.searchTerm && filters.searchTerm.trim().length) {
      const q = filters.searchTerm.trim().toLowerCase();
      base = base.filter((r) => {
        try {
          return JSON.stringify(r).toLowerCase().includes(q);
        } catch {
          return false;
        }
      });
    }

    return base;
  }, [
    brokers,
    shopRepFilters,
    selectedBrokerIds,
    filters.salesRep,
    filters.searchTerm,
  ]);

  useEffect(() => {
    fetchSummaryData();
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchSummaryData();
  }, [filters.fromDate, filters.toDate]);
  

  useEffect(() => {
    if (!filters.byPeriod) return;
  
    const { from, to } = getPeriodRange(filters.byPeriod);
  
    setFilters((prev) => ({
      ...prev,
      fromDate: from,
      toDate: to,
    }));
  }, [filters.byPeriod]);


  useEffect(() => {
    updateSummaryBoxes(shownRows);
  }, [shownRows]);

  const isBrokerLimitedLogin = () => {
    let userRole = localStorage.getItem("userRole");
    return userRole && userRole.toLowerCase() === "syndicator(limited)" ? true : false;
  };

  const updateFilters = (key: string, value: any) => {
    setFilters((prev: any) => ({ ...prev, [key]: value }));
  };

  const updateShopRep = (key: "shop" | "rep", value: boolean) => {
    setShopRepFilters((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    contactPerson: "",
    contactPersonId: "",
    address: "",
    state: "",
    city: "",
    zip: "",
    abaNumber: "",
    accountNumber: "",
    bankName: "",
    isBroker: true,
    isInvestor: false,
    isOverCollection: false,
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const validationRules: ValidationRules = {
    name: { required: true, message: "Name is required" },
    email: {
      required: false,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Valid email is required",
    },
    phoneNumber: {
      required: false,
      pattern: /^\(\d{3}\) \d{3}-\d{4}$/,
      message: "Valid phone number is required (e.g., (123)456-7890)",
    },
    contactPerson: { required: true, message: "Contact person is required" },
    address: { required: false, message: "Address is required" },
    state: { required: false, message: "State is required" },
    city: { required: false, message: "City is required" },
    zip: { required: false, message: "Valid ZIP/Postal code is required" },
    abaNumber: { required: false, message: "ABA Number is required", pattern: /^\d+$/ },
    accountNumber: { required: false, message: "Account Number is required" },
    bankName: { required: false, message: "Bank Name is required" },
  };


  const resetFormData = () => {
    setFormData({
      name: "",
      email: "",
      phoneNumber: "",
      contactPerson: "",
      contactPersonId: "",
      address: "",
      state: "",
      city: "",
      zip: "",
      abaNumber: "",
      accountNumber: "",
      bankName: "",
      isBroker: true,
      isInvestor: false,
      isOverCollection: false,
    });
    setErrors({});
  };

  const handleResetFilters = () => {
    setSelectedBrokerIds([]);
    setSelectedBrokerNames([]);
    setSelectedBroker(null);
    setFilters({
      fromDate: null,
      toDate: null,
      byPeriod: null,
      broker: null,
      searchTerm: "",
      salesRep: null,
    });
    setSelectedBrokerRow(null);
    setRangeOpen(false);
  };

  const handleSelectBroker = (ids: number[], names: string[]) => {
    setSelectedBrokerIds(ids);
    setSelectedBrokerNames(names);
    updateFilters("brokerIds", ids);
    updateFilters("brokerNames", names);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === "zip") {
      newValue = value.replace(/\D/g, "").slice(0, 5);
    } else if (name === "phoneNumber") {
      const numbers = value.replace(/\D/g, "");
      const char = numbers.split("");
      if (char.length === 0) newValue = "";
      else if (char.length <= 3) newValue = `(${char.join("")}`;
      else if (char.length <= 6)
        newValue = `(${char.slice(0, 3).join("")}) ${char.slice(3, 6).join("")}`;
      else
        newValue = `(${char.slice(0, 3).join("")}) ${char.slice(3, 6).join("")}-${char
          .slice(6, 10)
          .join("")}`;
      newValue = newValue.slice(0, 14);
    } else if (name === "abaNumber") {
      newValue = value.replace(/\D/g, "");
    }
    setFormData((prev) => ({ ...prev, [name]: newValue }));
    if (errors[name as keyof ValidationErrors]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const rule = validationRules[name];
    let error = "";
    if (rule && rule.required && !value.trim()) error = rule.message;
    else if (name === "zip" && value && !validateZipOrPostalCode(value, "US")) {
      // Default to US for brokers, but allow Canadian format as well
      if (!validateZipOrPostalCode(value, "CA")) {
        error = "5-digit ZIP code or Canadian postal code (A1A 1A1) required";
      }
    } else if (rule && rule.pattern && value && !rule.pattern.test(value)) error = rule.message;
    else if (rule && rule.validate && !rule.validate(value)) error = rule.message;
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleCloseModal = () => {
    resetFormData();
    setShowAddModal(false);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const fmtYMD = (d: Date | string | number | null | undefined) =>
    d ? moment(d).format("YYYY-MM-DD") : "";

  const buildSummaryUrl = (filters: any, getPeriodRangeFn: typeof getPeriodRange) => {
    let from: Date | null = filters.fromDate ? new Date(filters.fromDate) : null;
    let to: Date | null = filters.toDate ? new Date(filters.toDate) : null;
  
    if ((!from || !to) && filters.byPeriod) {
      const { from: pFrom, to: pTo } = getPeriodRangeFn(filters.byPeriod);
      from = from ?? pFrom;
      to = to ?? pTo;
    }
  
    const qs = new URLSearchParams();
    if (from) qs.set("fromDate", fmtYMD(from));
    if (to)   qs.set("toDate",   fmtYMD(to));
  
    const q = qs.toString();
    return `/fetchBrokerAndSalesRepSummary${q ? `?${q}` : ""}`;
  };

  const fetchSummaryData = async () => {
    try {
      setLoading(true);
  
      const url = buildSummaryUrl(filters as any, getPeriodRange);
      const { data = [] } = await get(url);
  
      const normalized = Array.isArray(data)
        ? data.map(({ isBrokerRow, lastSubmission, lastFunded, ...rest }: any, idx: number) => ({
            ...rest,
            uniqueId: `row-${idx + 1}`,
            isBroker: isBrokerRow,
            lastSubmissionRaw: lastSubmission ?? null,
            lastFundedRaw: lastFunded ?? null,
            lastSubmission: formatToMMDDYY(lastSubmission),
            lastFunded: formatToMMDDYY(lastFunded),
          }))
        : [];
  
      setBrokers(normalized);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  const fetchUsers = async () => {
    try {
      const tenantId = localStorage.getItem("tenantId") || "1";
      const response = await get(`/fetchUsers/${tenantId}`);
      if (response && Array.isArray(response)) setUsers(response);
    } catch {}
  };

  const validateForm = () => {
    const newErrors: ValidationErrors = {};
    let isValid = true;
    Object.keys(validationRules).forEach((key) => {
      const rule = validationRules[key];
      const value = formData[key as keyof typeof formData] as string;
      if (rule.required && !value.trim()) {
        newErrors[key as keyof ValidationErrors] = rule.message;
        isValid = false;
      } else if (key === "zip" && value && !validateZipOrPostalCode(value, "US")) {
        // Default to US for brokers, but allow Canadian format as well
        if (!validateZipOrPostalCode(value, "CA")) {
          newErrors[key as keyof ValidationErrors] = "5-digit ZIP code or Canadian postal code (A1A 1A1) required";
          isValid = false;
        }
      } else if (rule.pattern && value && !rule.pattern.test(value)) {
        newErrors[key as keyof ValidationErrors] = rule.message;
        isValid = false;
      } else if (rule.validate && value && !rule.validate(value)) {
        newErrors[key as keyof ValidationErrors] = rule.message;
        isValid = false;
      }
    });
    setErrors(newErrors);
    return isValid;
  };

  const handleSaveBroker = async () => {
    if (validateForm()) {
      try {
        setLoading(true);
        const brokerData = {
          isBroker: true,
          isInvestor: formData.isInvestor,
          name: formData.name,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          address: {
            address: formData.address,
            city: formData.city,
            zipCode: formData.zip,
            state: formData.state,
          },
          contactPerson: formData.contactPerson,
          contactPersonId: parseInt(formData.contactPersonId) || 0,
          abaNumber: formData.abaNumber,
          accountNumber: formData.accountNumber,
          bankName: formData.bankName,
          allowOverCollected: formData.isOverCollection,
          tenantId: localStorage.getItem("tenantId") || "1",
        };
        const createResponse = await post("/facilitator/createOrUpdateSyndicator", brokerData);
        if (createResponse && createResponse.result === true && createResponse.id) {
          await fetchSummaryData();
          setShowAddModal(false);
        } else {
          throw new Error("Failed to create/update broker: Invalid response format");
        }
      } catch {
      } finally {
        setLoading(false);
        resetFormData();
      }
    }
  };

  const handleDateChange = (filterType: string, date: any) => {
    setFilters((prev) => ({ ...prev, [filterType]: date }));
  };


  const handleViewDetails = (partyId: number) => {
    if (!partyId) return;
    window.location.href = `/#/broker/details/${partyId}`;
  };

  const asNumber = (v: any) => (typeof v === "number" ? v : Number(v) || 0);
  const latestDateFmt = (items: any[], key: "lastFundedRaw" | "lastSubmissionRaw") => {
    const ts = items.map((r) => r?.[key]).filter(Boolean) as (string | Date)[];
    if (!ts.length) return "-";
    const last = ts.reduce((a, b) => (new Date(b) > new Date(a) ? b : a));
    return formatToMMDDYY(last);
  };

  const computeTotals = (items: any[]) => {
    const asNumber = (v: any) => (typeof v === "number" ? v : Number(v) || 0);
    const sum = (k: string) => items.reduce((s, r) => s + asNumber(r?.[k]), 0);
  
    return {
      commissionsPaid: sum("commissionsPaid"),
      commissionsOwed: sum("commissionsOwed"),
      funded: sum("funded"),
      renewals: sum("renewals"),
      defaults: sum("defaults"),
      submissions: sum("submissions"),
      declined: sum("declined"),
      pendingResponse: sum("pendingResponse"),
      
      onTime: sum("onTime"),
      paidOff: sum("paidOff"),
      pendingApplications: sum("pendingApplications"),
  
      lastFunded: latestDateFmt(items, "lastFundedRaw"),
      lastSubmission: latestDateFmt(items, "lastSubmissionRaw"),
    };
  };

  const handleExportLedgerReport = async () => {
    try {
      setLedgerLoading(true);
      if (selectedBroker && selectedBroker.id) {
        const payload = { partyId: selectedBroker.id, isSyndicatorLimited: isBrokerLimitedLogin() };
        await post("/sendInvestorReport", payload);
      } else {
        await post("/sendMasterLedgerReport");
      }
      setShowLedgerModal(true);
    } catch {
    } finally {
      setLedgerLoading(false);
    }
  };

  const handleCloseLedgerModal = () => setShowLedgerModal(false);

  const DEFAULT_COLUMNS = [
    { Header: "Name", accessor: "name" },
    { Header: "Sales Rep", accessor: "salesRep" },
    { Header: "Email", accessor: "email" },
    { Header: "Phone", accessor: "phone" },
    { Header: "Last Funded", accessor: "lastFunded" },
    { Header: "Birthday", accessor: "birthday" },
    { Header: "Funded Deals", accessor: "funded" },
    { Header: "Last Submission", accessor: "lastSubmission" },
    { Header: "Submissions", accessor: "submissions" },
    { Header: "Declined", accessor: "declined" },
    { Header: "Defaults", accessor: "defaults" },
    { Header: "Pending Response", accessor: "pendingResponse" },
    { Header: "Commissions Paid", accessor: "commissionsPaid" },
    { Header: "Commissions Owed", accessor: "commissionsOwed" },
    { Header: "Renewals", accessor: "renewals" },
  ];

  const BROKER_ONLY_COLUMNS = DEFAULT_COLUMNS.filter((c) => c.accessor !== "salesRep");

  const handleExportData = (rows?: any[]) => {
    if (!rows || !Array.isArray(rows) || rows.length === 0) return;
    const shop = !!shopRepFilters?.shop;
    const rep = !!shopRepFilters?.rep;
    if (!shop && !rep) return;

    const filtered = rows
      .map((row) => row?.original ?? row)
      .filter((r) => {
        if (shop && rep) return true;
        if (shop && !rep) return !!r.isBroker;
        if (!shop && rep) return !r.isBroker;
        return false;
      });

    const columns = shop && !rep ? BROKER_ONLY_COLUMNS : DEFAULT_COLUMNS;

    const processedRows = filtered.map((r) => {
      const lastFundedRaw = r.lastFundedRaw ?? r.lastFunded ?? "";
      const lastSubmissionRaw = r.lastSubmissionRaw ?? r.lastSubmission ?? "";
      const birthdayRaw = r.birthday ?? "";
      return {
        name: r.name ?? "",
        salesRep: r.salesRep ?? "",
        email: r.email ?? "",
        phone: r.phone ?? "",
        lastFunded: formatServerDate(lastFundedRaw),
        birthday: formatServerDate(birthdayRaw),
        funded: r.funded ?? 0,
        lastSubmission: formatServerDate(lastSubmissionRaw),
        submissions: r.submissions ?? 0,
        declined: r.declined ?? 0,
        defaults: r.defaults ?? 0,
        pendingResponse: r.pendingResponse ?? 0,
        commissionsPaid: r.commissionsPaid ?? 0,
        commissionsOwed: r.commissionsOwed ?? 0,
        renewals: r.renewals ?? 0,
      };
    });

    ExcelExportUtil.exportToExcel(
      columns,
      processedRows,
      shop && !rep
        ? "Brokers Summary"
        : rep && !shop
        ? "Sales Rep Summary"
        : "Broker Sales Rep Summary",
      shop && !rep
        ? "brokers_summary.xlsx"
        : rep && !shop
        ? "sales_reps_summary.xlsx"
        : "broker_rep_summary.xlsx"
    );
  };

  return (
    <Box
      sx={{
        p: 2,
        height: "100vh",
        overflow: "hidden",
        marginLeft: "250px",
        width: "calc(100% - 250px)",
        transition: "all 0.3s ease",
      }}
    >
      {displayLedgerReportAlert && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Master ledger report has been generated and sent successfully.
        </Alert>
      )}

      {loading && <FullPageLoader />}

      <BrokersHeader
        onAddBroker={() => setShowAddModal(true)}
        onViewDetails={() => (selectedBroker ? handleViewDetails(selectedBroker.id) : null)}
        onResetFilters={handleResetFilters}
        onSelectBroker={handleSelectBroker}
        onDateChange={handleDateChange}
        onSearchChange={(v) => updateFilters("searchTerm", v)}
        onExportData={() => handleExportData(shownRows)}
        onExportLedgerReport={handleExportLedgerReport}
        selectedBrokerRow={selectedBrokerRow}
        selectedBroker={selectedBroker}
        selectedBrokerIds={selectedBrokerIds}
        selectedBrokerNames={selectedBrokerNames}
        rangeOpen={rangeOpen}
        setRangeOpen={setRangeOpen}
        filters={filters}
        data={brokers}
        ledgerLoading={ledgerLoading}
      />

      <Card
        elevation={3}
        sx={{
          width: "100%",
          mb: 2,
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          ml: 1,
          paddingBottom: "20px",
        }}
      >
        <CardContent sx={{ p: 1 }}>
          <SummaryRow
            items={summaryBoxes.map((b) => ({ title: b.title, value: b.value, color: b.color }))}
          />

          <Box display="flex" alignItems="center" gap={2} flexWrap="nowrap" px={2} mt={1} mb={-4}>
            {filterOptions.map((filter: any) => (
              <FilterCheckbox
                key={filter.value}
                label={filter.label}
                checked={!!shopRepFilters[filter.value]}
                onChange={(checked: boolean) => updateShopRep(filter.value, checked)}
              />
            ))}
          </Box>

          <Box
            sx={{
              flex: "1 1 auto",
              marginTop: "40px",
              display: "flex",
              flexDirection: "column",
              overflow: "visible",
              paddingBottom: "30px",
            }}
          >
            <CollapsibleBrokersWrapper
              data={shownRows}
              selected={selectedBroker}
              setSelected={setSelectedBroker}
              showBrokers={!!shopRepFilters.shop}
              showReps={ !!shopRepFilters.rep}
              onDoubleClick= {handleViewDetails}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={showAddModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            border: "2px solid #555",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          },
        }}
      >
        <DialogTitle>BROKER DETAILS</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Name*"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.name}
                helperText={errors.name}
                variant="standard"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.email}
                helperText={errors.email}
                variant="standard"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                variant="standard"
              />
            </Grid>
            <Grid item xs={5}>
              <FormControl fullWidth variant="standard" error={!!errors.contactPerson}>
                <InputLabel>Contact Person*</InputLabel>
                <Select
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={(e) => {
                    const selectedUser = users.find(
                      (user) => `${user.firstName} ${user.lastName}` === e.target.value
                    );
                    setFormData((prev) => ({
                      ...prev,
                      contactPerson: e.target.value,
                      contactPersonId: selectedUser ? selectedUser.userId.toString() : "",
                    }));
                  }}
                  onBlur={() => {
                    const v = formData.contactPerson;
                    const rule = validationRules["contactPerson"];
                    let error = "";
                    if (rule && rule.required && !v.trim()) error = rule.message;
                    setErrors((prev) => ({ ...prev, contactPerson: error }));
                  }}
                  onOpen={() => setdropdownOpen(true)}
                  onClose={() => setdropdownOpen(false)}
                  endAdornment={dropdownOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                >
                  {users.map((user) => (
                    <MenuItem key={user.userId} value={`${user.firstName} ${user.lastName}`}>
                      {user.firstName} {user.lastName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.contactPerson && <FormHelperText>{errors.contactPerson}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={7}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.address}
                helperText={errors.address}
                variant="standard"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.city}
                helperText={errors.city}
                variant="standard"
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth variant="standard" error={!!errors.state}>
                <InputLabel>State*</InputLabel>
                <Select
                  name="state"
                  value={formData.state}
                  onChange={handleSelectChange}
                  onBlur={() => {
                    const v = formData.state;
                    const rule = validationRules["state"];
                    let error = "";
                    if (rule && rule.required && !String(v || "").trim()) error = rule.message;
                    setErrors((prev) => ({ ...prev, state: error }));
                  }}
                  onOpen={() => setdropdownOpen(true)}
                  onClose={() => setdropdownOpen(false)}
                  endAdornment={dropdownOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                >
                  {states.map((state) => (
                    <MenuItem key={state.short} value={state.value}>
                      {state.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.state && <FormHelperText>{errors.state}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Zip Code"
                name="zip"
                value={formData.zip}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.zip}
                helperText={errors.zip}
                variant="standard"
                inputProps={{ maxLength: 5, pattern: "[0-9]*", inputMode: "numeric" }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="BANK NAME"
                name="bankName"
                value={formData.bankName}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.bankName}
                helperText={errors.bankName}
                variant="standard"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="ABA NUMBER"
                name="abaNumber"
                value={formData.abaNumber}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.abaNumber}
                helperText={errors.abaNumber}
                variant="standard"
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 9 }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="ACCOUNT NUMBER"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.accountNumber}
                helperText={errors.accountNumber}
                variant="standard"
              />
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ display: "flex", alignItems: "center", height: "100%", pt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isInvestor"
                      checked={formData.isInvestor}
                      onChange={handleCheckboxChange}
                      sx={{
                        "& .MuiSvgIcon-root": { border: "1px solid #666", borderRadius: "3px" },
                      }}
                    />
                  }
                  label="Is Investor"
                />
              </Box>
            </Grid>
            <Grid item xs={3.5}>
              <Box sx={{ display: "flex", alignItems: "center", height: "100%", pt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isOverCollection"
                      checked={formData.isOverCollection}
                      onChange={handleCheckboxChange}
                      sx={{
                        "& .MuiSvgIcon-root": { border: "1px solid #666", borderRadius: "3px" },
                      }}
                    />
                  }
                  label="Allow Over-collection"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton variant="contained" color="error" onClick={handleCloseModal} disabled={loading}>
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            color="primary"
            onClick={handleSaveBroker}
            disabled={loading || Object.values(errors).some((e) => e)}
          >
            Save
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showLedgerModal}
        onClose={handleCloseLedgerModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "8px", padding: "20px 20px 16px 20px", textAlign: "center" },
        }}
      >
        <DialogContent sx={{ padding: "10px 10px 4px 10px" }}>
          <Typography variant="body1" sx={{ color: "#333", fontSize: "18px" }}>
            {!selectedBroker
              ? "Master ledger report generation initiated in the background and will be emailed to you on completion."
              : `Report generation for ${selectedBroker.name} initiated in the background and will be emailed to you on completion.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", padding: "10px 10px 16px 10px" }}>
          <Button
            onClick={handleCloseLedgerModal}
            variant="outlined"
            sx={{
              border: "1px solid #ccc",
              borderRadius: "4px",
              color: "#1976d2",
              padding: "8px 24px",
              textTransform: "none",
              fontSize: "18px",
              fontWeight: 400,
              "&:hover": {
                borderColor: "#1976d2",
                backgroundColor: "rgba(25, 118, 210, 0.04)",
                color: "#1976d2",
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default withDommer(Brokers);
